import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

const IMAGE_CDN_BASE_URL = process.env.REACT_APP_IMAGE_CDN_SEO_URL;

const getSeoImageUrl = (imagePath) => {
  if (!imagePath) {
    return "";
  }

  if (/^https?:\/\//i.test(imagePath)) {
    return imagePath;
  }

  const normalizedPath = imagePath.startsWith("/")
    ? imagePath
    : `/${imagePath}`;

  if (IMAGE_CDN_BASE_URL) {
    return `${IMAGE_CDN_BASE_URL}${normalizedPath}`;
  }

  if (typeof window !== "undefined") {
    return `${window.location.origin}${normalizedPath}`;
  }

  return normalizedPath;
};

const getAbsoluteUrl = (pathOrUrl) => {
  if (!pathOrUrl) {
    return "";
  }

  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  const normalizedPath = pathOrUrl.startsWith("/")
    ? pathOrUrl
    : `/${pathOrUrl}`;

  if (typeof window !== "undefined") {
    return `${window.location.origin}${normalizedPath}`;
  }

  return normalizedPath;
};

const DEV_HOSTNAMES = ["dev-orochi.pgadm.com"];

export const SEO = ({ routeKey, item }) => {
  const { t: tSeo } = useTranslation("seo");
  const { t: tRoutes } = useTranslation("routes");
  const isDevEnvironment =
    typeof window !== "undefined" &&
    DEV_HOSTNAMES.includes(window.location.hostname);
  const seoEntry = routeKey
    ? tSeo(routeKey, { returnObjects: true })
    : {};
  const routePath = routeKey ? tRoutes(routeKey) : "";
  const routePathEn = routeKey ? tRoutes(routeKey, { lng: "en" }) : "";
  const routePathFr = routeKey ? tRoutes(routeKey, { lng: "fr" }) : "";

  const resolvedItem = {
    ...seoEntry,
    ...item,
  };

  const canonicalUrl = resolvedItem.url || getAbsoluteUrl(routePath);
  const alternateUrlEn =
    resolvedItem.urlEn || (routePathEn ? getAbsoluteUrl(routePathEn) : "");
  const alternateUrlFr =
    resolvedItem.urlFr || (routePathFr ? getAbsoluteUrl(routePathFr) : "");
  const hasDistinctAlternates =
    alternateUrlEn &&
    alternateUrlFr &&
    alternateUrlEn !== alternateUrlFr;

  return (
    <Helmet>
      {isDevEnvironment && (
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
      )}
      <title>{resolvedItem.title}</title>
      <meta name="description" content={resolvedItem.description} />
      <meta property="og:type" content="webapp" />
      <meta property="og:title" content={resolvedItem.title} />
      {resolvedItem.image && (
        <meta property="og:image" content={getSeoImageUrl(resolvedItem.image)} />
      )}
      <meta property="og:description" content={resolvedItem.description} />
      {resolvedItem.keywords && (
        <meta name="keywords" content={resolvedItem.keywords} />
      )}

      {!isDevEnvironment && canonicalUrl && (
        <>
          <link rel="canonical" href={canonicalUrl} />
          {hasDistinctAlternates && (
            <>
              <link rel="alternate" hreflang="en" href={alternateUrlEn} />
              <link rel="alternate" hreflang="fr" href={alternateUrlFr} />
            </>
          )}
        </>
      )}
    </Helmet>
  );
};
